import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useRef, useState } from "react"
import { FaPlus } from "react-icons/fa"

import { useCreateStudent } from "@/hooks/api/useStudents"
import { usePermissions } from "@/hooks/usePermissions"
import { studentUpdateSchema } from "@/types/student"
import type { AddStudentProps, StudentCreateInput, StudentUpdateInput } from "@/types/student"
import { Button } from "@chakra-ui/react"

import StudentFormDialog from "./StudentFormDialog"

const AddStudent = memo(({ onSuccess, onCancel }: AddStudentProps = {}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { hasPermission } = usePermissions()
  const createStudentMutation = useCreateStudent()
  const addButtonRef = useRef<HTMLButtonElement | null>(null)

  const defaultValues = useMemo<StudentUpdateInput>(
    () => ({
      first_name: "",
      last_name: "",
      date_of_birth: "",
      gender: null,
      household_id: "",
      current_swim_level: null,
      skill_level_notes: "",
      has_asthma: false,
      preferred_location: null,
      notes: "",
      is_active: true,
      photo_url: "",
    }),
    [],
  )

  const handleClose = () => {
    setIsOpen(false)
    onCancel?.()
  }

  const handleSubmit = async (data: StudentUpdateInput) => {
    // Cast to CreateInput since all required fields will be present after form validation
    await createStudentMutation.mutateAsync(data as StudentCreateInput)
    onSuccess?.()
  }

  // Check permission after all hooks are called
  if (!hasPermission("students:create")) {
    return null
  }

  return (
    <>
      <Button
        ref={addButtonRef}
        onClick={() => setIsOpen(true)}
        colorPalette="blue"
        size="sm"
        aria-label="Add new student"
      >
        <FaPlus fontSize="14px" aria-hidden="true" />
        Add Student
      </Button>

      <StudentFormDialog
        isOpen={isOpen}
        title="Add New Student"
        submitLabel="Create Student"
        resolver={zodResolver(studentUpdateSchema)}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onClose={handleClose}
        isSubmitting={createStudentMutation.isPending}
        finalFocusRef={addButtonRef}
        showSkipLink
        loadingText="Creating..."
      />
    </>
  )
})

AddStudent.displayName = "AddStudent"

export default AddStudent
export type { AddStudentProps }
